import { ArticleDTO, ArticleModel } from "@/entities/Article";
import { MediaDTO, MediaModel } from "@/entities/Media";

import normalizeString from "@/utils/normalizeString";
import { shallowRef } from "vue";
import useAPIRequest from "@/composables/useAPIRequest";

const fetchSearchResultsQuery = `
{
  'articles': *[
    _type == "article"
    && !(_id in path("drafts.**"))
    && date <= now()
    && defined(slug)
    && defined(image)
    && defined(title)
    && defined(body)
    && (
      title match $query
      || title match $query + "*"
      || title match $queryWithoutDiacritics
      || slug.current match $query
      || slug.current match $queryWithoutDiacritics
      || pt::text(body) match $query
      || pt::text(body) match $query + "*"
      || pt::text(body) match $queryWithoutDiacritics
    )
  ] {
    _id,
    title,
    "slug": slug.current,
    date,
    "image": image.asset->url,
    "relevanceScore": (
      (title match $query) * 1 +
      (title match $query + "*") * 1 +
      (title match $queryWithoutDiacritics) * 1 +
      (slug.current match $query) * 1 +
      (slug.current match $queryWithoutDiacritics) * 1 +
      (pt::text(body) match $query) * 0.25 +
      (pt::text(body) match $query + "*") * 0.25 +
      (pt::text(body) match $queryWithoutDiacritics) * 0.25
    )
  } | order(relevanceScore desc),
  'medias': *[
    _type == "media"
    && !(_id in path("drafts.**"))
    && defined(title)
    && defined(slug)
    && defined(type)
    && defined(premierDate)
    && defined(poster)
    && defined(body)
    && (
      title match $query
      || title match $query + "*"
      || title match $queryWithoutDiacritics
      || slug.current match $query
      || slug.current match $queryWithoutDiacritics
      || year match $query
      || directors match $query
      || directors match $queryWithoutDiacritics
      || genres match $query
      || genres match $queryWithoutDiacritics
      || countries match $query
      || countries match $queryWithoutDiacritics
      || pt::text(body) match $query
      || pt::text(body) match $query + "*"
      || pt::text(body) match $queryWithoutDiacritics
    )
  ] {
    _id,
    title,
    "slug": slug.current,
    type,
    isFeatured,
    premierDate,
    "poster": poster.asset->url,
    "relevanceScore": (
      (title match $query) * 1 +
      (title match $query + "*") * 1 +
      (title match $queryWithoutDiacritics) * 0.5 +
      (slug.current match $query) * 1 +
      (slug.current match $queryWithoutDiacritics) * 1 +
      (year match $query) * 1 +
      (directors match $query) * 1 +
      (directors match $queryWithoutDiacritics) * 0.25 +
      (genres match $query) * 1 +
      (genres match $queryWithoutDiacritics) * 0.25 +
      (countries match $query) * 1 +
      (countries match $queryWithoutDiacritics) * 0.25 +
      (pt::text(body) match $query) * 0.25 +
      (pt::text(body) match $query + "*") * 0.25 +
      (pt::text(body) match $queryWithoutDiacritics) * 0.25
    )
  } | order(relevanceScore desc)
}`;

export default function useSearchResults() {
  const { fetch, requestState } = useAPIRequest<
    { articles: ArticleDTO[]; medias: MediaDTO[] },
    { query: string; queryWithoutDiacritics: string }
  >();

  const searchResults = shallowRef<{
    articles: ArticleModel[];
    medias: MediaModel[];
  }>({ articles: [], medias: [] });

  const fetchSearchResults = async (query: string) => {
    await fetch(fetchSearchResultsQuery, {
      query,
      queryWithoutDiacritics: normalizeString(query),
    });
    const { articles: articleDTOs, medias: mediaDTOs } = requestState.value
      .data || { articles: [], medias: [] };
    searchResults.value = {
      articles: articleDTOs.map((articleDTO) => new ArticleModel(articleDTO)),
      medias: mediaDTOs.map((mediaDTO) => new MediaModel(mediaDTO)),
    };
  };

  return {
    searchResults,
    fetchSearchResults,
  };
}
